import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { ApiResponse } from '../../shared/types';

/**
 * Vendors svc-vendor-sync can pull from.
 *
 * The union names the adapters this build knows about so call sites keep
 * autocomplete, but the UI renders from `listVendors()` at runtime rather than
 * from this type — a backend that ships a new adapter surfaces it without a
 * frontend release.
 *
 * `(string & {})` is what admits those runtime names while preserving the
 * literal suggestions; a plain `| string` would collapse the union and lose
 * them. Adapter names are only ever used to build a URL path and are echoed by
 * the server, so widening costs no safety here.
 */
export type VendorName = 'sitewatch' | 'everwash' | (string & {});

/**
 * One credential input a vendor needs, as declared by its adapter.
 *
 * The credential form is rendered from this rather than a per-vendor template,
 * so vendors with different login shapes (SiteWatch's employee code, EverWash's
 * email) share one form. `secret: true` marks a value that must never be echoed
 * back to the UI once stored.
 */
export interface VendorCredentialField {
  key: string;
  label: string;
  type: 'text' | 'email' | 'password';
  required: boolean;
  secret: boolean;
  hint?: string;
}

/** Non-secret description of one adapter, from GET /vendor-sync/vendors. */
export interface VendorAdapterInfo {
  name: string;
  displayName: string;
  credentialFields: VendorCredentialField[];
  /**
   * Whether this vendor may be executed by the laptop agent. Comes from the
   * backend descriptor rather than a list held here, so the toggle can never
   * offer a combination the executor route rejects. Absent on older backends,
   * which is treated as false.
   */
  agentCapable?: boolean;
  /**
   * Which executor this vendor SHOULD use — distinct from `agentCapable`, which
   * is only whether it can.
   *
   * Every adapter is agent-capable, but only one (iWashPOS) actually needs the
   * laptop; the rest pull from the cluster and get strictly worse on a machine
   * that has to be awake. The picker reads this to say which option is the
   * right one rather than presenting two equal buttons.
   *
   * Absent on older backends — treat as 'server', which is both the safe
   * default and what those backends assumed.
   */
  recommendedExecutor?: 'server' | 'agent';
}

export interface VendorListResponse {
  vendors: string[];
  adapters?: VendorAdapterInfo[];
}

/**
 * Credential state for a company.
 *
 * Deliberately contains no credential values — the API returns booleans plus
 * the non-secret locationCode, so an admin session cannot be used to read back
 * stored vendor passwords.
 */
/**
 * How this shop's vendor memberships may be redeemed at the lane.
 *
 * Mirrors libs/core vendorPlateMatching — that module is the contract, this is the
 * client-side view of it.
 */
export interface VendorPlateMatching {
  /**
   * When false, a camera plate read no longer redeems silently: the visit falls
   * through to create-order-request and an attendant decides. A deliberate barcode
   * scan still redeems, so this governs the automatic path, not the entitlement.
   */
  autoRedeem: boolean;
  /**
   * When false, the Active subscription modal hides Collect payment / card-on-file.
   * Default false (opt-in). Barcode redeem still works.
   */
  collectPayment: boolean;
  /**
   * CWS-1369. When false, the order form drops "Redeem Existing Membership" — the barcode
   * reader and camera scanner an attendant uses to look a member up by their card.
   *
   * Tri-state at rest, resolved to a boolean by the time it reaches here: an unset value
   * falls back to whether this vendor issues a scannable token, so DRB/EverWash/Washify
   * read true and iWashPOS reads false until a super admin says otherwise.
   */
  orderFormRedeem: boolean;
  /**
   * Locations where this vendor's memberships may be redeemed at all — camera or
   * barcode. Empty means unrestricted, NOT "nowhere".
   */
  locationIds: string[];
}

export interface VendorCredentialStatus {
  configured: boolean;
  hasToken: boolean;
  hasLogin: boolean;
  /**
   * Whether this shop's sync for this vendor is live. False either because a
   * super-admin paused it or because no credentials exist yet — only meaningful
   * alongside `configured`. Pausing stops the nightly sweep and the manual
   * trigger and hides vendor UI; it does not touch imported data.
   */
  enabled: boolean;
  /**
   * Whether the nightly sweep includes this shop. Independent of `enabled`:
   * turning it off still allows Sync now and keeps the staff app's vendor UI —
   * it is a canary's manual-only window, and nightly is the normal state.
   *
   * Optional for a rolling deploy against a svc-vendor-sync that predates it;
   * absent means on, matching the server, which only treats `false` as off.
   */
  nightlySync?: boolean;
  locationCode: string | null;
  /**
   * Lane redeem controls. Distinct from `enabled`, which governs the nightly sync:
   * these govern whether an imported membership can be redeemed at the gate, and
   * stay in force while the sync is paused.
   *
   * Optional because it is not optional on the server — it is always sent, fully
   * resolved — but during a rolling deploy this bundle can be talking to a
   * svc-vendor-sync that predates the field. Callers must therefore default it
   * rather than assume presence; see ImportDataComponent.plateMatching.
   */
  plateMatching?: VendorPlateMatching;
  updatedAt?: string | null;
  updatedBy?: string | null;
  lastVerifiedAt?: string | null;
  /**
   * False when VENDOR_SYNC_ENCRYPTION_KEY is missing or the wrong length. Saving
   * and syncing both fail in that state, so the UI warns up front.
   */
  encryptionConfigured: boolean;
  /** Present when the credential store could not be read at all. */
  unavailableReason?: string;
  /**
   * Who pulls this vendor: in-cluster worker (`server`) or laptop agent (`agent`).
   * Defaults to server when absent (older backends / unset meta).
   */
  executor?: 'server' | 'agent';
}

/**
 * Credentials submitted for test or save. Never persisted client-side.
 *
 * Keyed loosely because the field set is the adapter's to define — the server
 * accepts only the keys that adapter declares, so an any key here is
 * dropped rather than stored.
 */
export interface VendorCredentialInput {
  [field: string]: string | undefined;
  employeeCode?: string;
  locationCode?: string;
  email?: string;
  serviceLocationId?: string;
  password?: string;
  token?: string;
  baseUrl?: string;
}

export interface VendorCredentialTestResult {
  ok: boolean;
  /** Site name or code echoed by the vendor, to confirm the right connection. */
  location: string | null;
  expiresAt: string | null;
  /**
   * Members the vendor reports for this site, when its adapter reads the roster
   * during the test rather than only checking the login. Lets an operator spot a
   * connection to the wrong site before they save it.
   */
  memberCount?: number;
  reportedTotal?: number | null;
}

export interface VendorSyncJobCreateResponse {
  jobId: string;
}

export interface VendorSyncJobProgress {
  vendor?: string;
  companyId?: string;
  /** Total members the vendor report says it holds, once the first page lands. */
  reportTotal?: number | null;
  rowsSent?: number;
  chunks?: number;
  inserted?: number;
  updated?: number;
  duplicates?: number;
  errors?: number;
  fetched?: number;
  skipped?: number;
  jobIds?: string[];
  /**
   * Set by the worker when this shop's vendor runs on the laptop agent. The
   * BullMQ job then finishes within a second — it only enqueues the durable
   * agent job and releases the worker slot — so these two fields are the whole
   * progress story it will ever carry, and `agentJobId` is where the real one
   * continues. Callers following a sync must hand over to `getAgentJob`.
   */
  delegated?: boolean;
  agentJobId?: string;
}

/**
 * A registered laptop agent, as `GET /vendor-sync/agents` returns it.
 *
 * `connected` is an SSE stream attached right now; `lastSeenAt` is the last
 * request of any kind. They are different facts and the UI shows both — an
 * agent that ran a job hours ago but is powered off is `connected: false` with
 * a recent-ish `lastSeenAt`.
 */
export interface VendorAgentSummary {
  id: string;
  name: string;
  connected: boolean;
  lastSeenAt?: string | null;
  agentVersion?: string | null;
  /**
   * Adapter keys this agent reported it can run, observed from its own bundle.
   *
   * `null`/absent means the agent did not report — an image built before
   * capability reporting — and must NOT be rendered as "runs nothing". The
   * distinction is what lets the UI say "this laptop is too old to tell us"
   * instead of wrongly accusing it of missing every adapter.
   */
  supportedVendors?: string[] | null;
}

/** What started a sync: the nightly sweep, or Sync now. Older jobs carry neither. */
export type VendorSyncTrigger = 'schedule' | 'manual';

/** The run options stored on an agent job (svc-vendor-sync's `options` jsonb). */
export interface VendorAgentJobOptions {
  limit?: number | null;
  trigger?: VendorSyncTrigger | null;
}

/**
 * One row of `vendor_sync_agent_jobs`.
 *
 * `counts` is the agent's own summary and carries the same field names the
 * server path publishes to BullMQ progress, so it maps onto the same UI.
 */
export interface VendorAgentJob {
  id: string;
  status: string;
  options?: VendorAgentJobOptions | null;
  counts?: VendorSyncJobProgress | null;
  errorMessage?: string | null;
  heartbeatAt?: string | null;
  createdAt?: string;
  completedAt?: string | null;
  vendor?: string;
  companyId?: string;
  parts?: { jobId: string; rows: number }[] | null;
}

export interface VendorSyncJobResponse {
  jobId: string;
  state: string;
  vendor?: string;
  companyId?: string;
  progress: VendorSyncJobProgress | null;
  failedReason: string | null;
  attemptsMade?: number;
}

@Injectable({
  providedIn: 'root',
})
export class VendorSyncService {
  private readonly apiService = inject(ApiService);

  private credentialsPath(vendor: VendorName, companyId: string): string {
    return `vendor-sync/${vendor}/companies/${companyId}/credentials`;
  }

  /**
   * Which adapters this build of svc-vendor-sync supports, and what credential
   * fields each needs.
   *
   * Static metadata only — no company scope and no credential store access, so
   * this cannot leak a stored value.
   */
  listVendors(): Observable<ApiResponse<VendorListResponse>> {
    return this.apiService
      .getWithSuperAdminToken<VendorListResponse>('vendor-sync/vendors')
      .pipe(catchError(error => throwError(() => error)));
  }

  /** Whether credentials exist for this company — never their values. */
  getCredentialStatus(
    vendor: VendorName,
    companyId: string
  ): Observable<ApiResponse<VendorCredentialStatus>> {
    return this.apiService
      .getWithSuperAdminToken<VendorCredentialStatus>(this.credentialsPath(vendor, companyId))
      .pipe(catchError(error => throwError(() => error)));
  }

  /**
   * Verify a login without saving it, so an operator can confirm the credentials
   * and the location code before storing them.
   */
  testCredentials(
    vendor: VendorName,
    companyId: string,
    credentials: VendorCredentialInput
  ): Observable<ApiResponse<VendorCredentialTestResult>> {
    return this.apiService
      .postWithSuperAdminToken<VendorCredentialTestResult>(
        `${this.credentialsPath(vendor, companyId)}/test`,
        credentials
      )
      .pipe(catchError(error => throwError(() => error)));
  }

  saveCredentials(
    vendor: VendorName,
    companyId: string,
    credentials: VendorCredentialInput
  ): Observable<ApiResponse<null>> {
    return this.apiService
      .putWithSuperAdminToken<null>(this.credentialsPath(vendor, companyId), credentials)
      .pipe(catchError(error => throwError(() => error)));
  }

  deleteCredentials(vendor: VendorName, companyId: string): Observable<ApiResponse<null>> {
    return this.apiService
      .deleteWithSuperAdminToken<null>(this.credentialsPath(vendor, companyId))
      .pipe(catchError(error => throwError(() => error)));
  }

  /** Turn the nightly sweep on or off for this shop without pausing it. */
  setNightlySync(
    vendor: VendorName,
    companyId: string,
    nightlySync: boolean
  ): Observable<ApiResponse<VendorCredentialStatus>> {
    return this.apiService
      .patchWithSuperAdminToken<VendorCredentialStatus>(
        `vendor-sync/${vendor}/companies/${companyId}/nightly`,
        { nightlySync }
      )
      .pipe(catchError(error => throwError(() => error)));
  }

  /**
   * Pause or resume this shop's sync without discarding its credentials or plan
   * mapping — deleting the credentials was the only way to withdraw a shop before
   * this existed, and it lost the stored login and every mapped plan.
   *
   * Returns the refreshed credential status so the caller can render from one
   * source of truth rather than assuming the write succeeded.
   */
  setSyncEnabled(
    vendor: VendorName,
    companyId: string,
    enabled: boolean
  ): Observable<ApiResponse<VendorCredentialStatus>> {
    return this.apiService
      .patchWithSuperAdminToken<VendorCredentialStatus>(
        `vendor-sync/${vendor}/companies/${companyId}/enabled`,
        { enabled }
      )
      .pipe(catchError(error => throwError(() => error)));
  }

  /**
   * Update the lane redeem controls.
   *
   * Both fields are optional and omitting one keeps its stored value, so the toggle
   * can be flipped without resending the location list. Passing `locationIds: []`
   * is meaningful — it clears the restriction.
   *
   * Returns the refreshed credential status, same as setSyncEnabled, so the caller
   * renders from the server's resolved values rather than its own optimistic guess.
   */
  setPlateMatching(
    vendor: VendorName,
    companyId: string,
    patch: Partial<VendorPlateMatching>
  ): Observable<ApiResponse<VendorCredentialStatus>> {
    return this.apiService
      .patchWithSuperAdminToken<VendorCredentialStatus>(
        `vendor-sync/${vendor}/companies/${companyId}/plate-matching`,
        patch
      )
      .pipe(catchError(error => throwError(() => error)));
  }

  /**
   * Queue a one-off sync. Credentials are resolved server-side from the store —
   * they are never sent with this request.
   */
  triggerSync(
    vendor: VendorName,
    companyId: string,
    options?: { limit?: number | null }
  ): Observable<ApiResponse<VendorSyncJobCreateResponse>> {
    const payload: Record<string, string | boolean | number> = {};
    // Canary row cap. Omitted, not sent as null, so a full sync looks exactly as it always has.
    const limit = options?.limit;
    if (typeof limit === 'number' && Number.isInteger(limit) && limit > 0) {
      payload['limit'] = limit;
    }
    return this.apiService
      .postWithSuperAdminToken<VendorSyncJobCreateResponse>(
        `vendor-sync/${vendor}/companies/${companyId}/sync`,
        payload
      )
      .pipe(catchError(error => throwError(() => error)));
  }

  getSyncJob(jobId: string): Observable<ApiResponse<VendorSyncJobResponse>> {
    return this.apiService
      .getWithSuperAdminToken<VendorSyncJobResponse>(`vendor-sync/jobs/${jobId}`)
      .pipe(catchError(error => throwError(() => error)));
  }

  /**
   * Set whether this shop's vendor is pulled by the cloud worker or the laptop agent.
   */
  setExecutor(
    vendor: VendorName,
    companyId: string,
    executor: 'server' | 'agent'
  ): Observable<ApiResponse<{ vendor: string; companyId: string; executor: string }>> {
    return this.apiService
      .patchWithSuperAdminToken(`vendor-sync/${vendor}/companies/${companyId}/executor`, {
        executor,
      })
      .pipe(catchError(error => throwError(() => error)));
  }

  listAgents(): Observable<ApiResponse<{ agents: VendorAgentSummary[] }>> {
    return this.apiService
      .getWithSuperAdminToken(`vendor-sync/agents`)
      .pipe(catchError(error => throwError(() => error)));
  }

  getLatestAgentJob(
    vendor: VendorName,
    companyId: string
  ): Observable<ApiResponse<{ job: VendorAgentJob | null }>> {
    return this.apiService
      .getWithSuperAdminToken(`vendor-sync/${vendor}/companies/${companyId}/agent-job`)
      .pipe(catchError(error => throwError(() => error)));
  }

  /**
   * One agent job by id — the progress source for a delegated sync.
   *
   * Addressed by id rather than reusing `getLatestAgentJob`, because "latest"
   * is a different job the moment the nightly sweep enqueues one: a poll that
   * re-reads "latest" every 2.5s would silently start reporting another run's
   * counts under the operator's job.
   */
  getAgentJob(agentJobId: string): Observable<ApiResponse<VendorAgentJob>> {
    return this.apiService
      .getWithSuperAdminToken(`vendor-sync/agent-jobs/${agentJobId}`)
      .pipe(catchError(error => throwError(() => error)));
  }
}
